import './LeaveRequestView.scss';
import { useUser } from '../context/UserContext';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import {
  formatEnumLabel,
  getRequestStatusType,
  Notify,
  RequestStatus,
} from '../utils/constants';
import { useCallback, useEffect, useState } from 'react';
import { LeaveRequest } from '../utils/types';
import { Button, Spin, Tag, Tooltip, Typography } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import RequestStatusModal from '../components/Modals/RequestStatusModal';
import { ViewDocumentsBtn } from '../components/Buttons/ViewDocumentsBtn';
import { apiClient } from '../utils/httpClient';
import { canReviewAsManager } from '../utils/helpers';
import { format } from 'date-fns';

function formatDateAsUTC(date: Date): string {
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  return format(utcDate, 'dd/MM/yyyy');
}

// Statuses where a decision is actually pending - the only ones this page
// offers Approve/Reject for. RequestStatusModal derives which endpoint to
// call (Manager vs Administrativ) from the request's own status, so both are
// safe to route through the same two buttons; every other status (final
// outcomes, awaiting document review, returned for correction, ...) needs a
// different action this simple page doesn't attempt to reproduce - the user
// is directed to the app instead.
const ACTIONABLE_STATUSES = new Set(['AWAITING_MANAGER', 'AWAITING_ADMINISTRATIV']);

// A request is "intermediate" as long as its outcome isn't settled yet -
// everything except the 4 final outcomes below. approverName falls back to
// the currently-assigned Manager while pending (see
// LeaveRequestQueryService#effectiveApprover), so it's only shown as the
// "Task assigned to" line while intermediate, and as the "Approver" line
// (alongside administrativApproverName) once finalized.
const FINALIZED_STATUSES = new Set([
  'APPROVED',
  'REJECTED',
  'CANCELLED',
  'CANCELLATION_REJECTED',
]);
const MANAGER_PENDING_STATUSES = new Set([
  'AWAITING_MANAGER',
  'AWAITING_MANAGER_CANCELLATION',
]);
// Administrativ reviews from a shared role queue, not a per-request assignee
// - request.approver is always the employee's Manager (or their active OoO
// substitute), never an Administrativ user, so there's no specific name to
// show here, only the responsible team.
const ADMINISTRATIV_PENDING_STATUSES = new Set([
  'AWAITING_ADMINISTRATIV',
  'AWAITING_ADMINISTRATIV_CANCELLATION',
  'AWAITING_DOCUMENT_REVIEW',
]);

const LeaveRequestView = () => {
  const { authUser, roles } = useUser();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // Table "Request ID" links append ?view=1 to force a read-only view (no
  // Approve/Reject) regardless of the request's status - the email link
  // (no query param) keeps today's behavior of offering the action when
  // the request is actionable.
  const forceReadOnly = searchParams.get('view') === '1';
  const [leaveRequest, setLeaveRequest] = useState<LeaveRequest | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [isRequestStatusOpen, setIsRequestStatusOpen] =
    useState<boolean>(false);
  const [status, setStatus] = useState<RequestStatus>(
    RequestStatus.AWAITING_MANAGER,
  );

  const fetchLeaveRequest = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setForbidden(false);
      const response = await apiClient.get(
        `${process.env.REACT_APP_API_URL}/leave-requests/${id}`,
      );
      setLeaveRequest(response.data);
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 403) {
        setForbidden(true);
      } else {
        console.error('Error fetching leave request details:', error);
        Notify('error', 'Failed to fetch leave request details.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!authUser) return;
    fetchLeaveRequest();
  }, [authUser, fetchLeaveRequest]);

  const isActionable =
    !forceReadOnly &&
    !!leaveRequest &&
    ACTIONABLE_STATUSES.has(leaveRequest.requestStatus) &&
    // HR can open any request, but only the employee's manager (or their OoO
    // substitute) / ADMIN may decide the Manager step.
    (leaveRequest.requestStatus !== 'AWAITING_MANAGER' ||
      canReviewAsManager(
        leaveRequest,
        authUser?.email,
        roles.some((role) => role.replace(/^ROLE_/, '') === 'ADMIN'),
      ));

  const isIntermediateStatus =
    !!leaveRequest && !FINALIZED_STATUSES.has(leaveRequest.requestStatus);

  const assignedTo = (() => {
    if (!leaveRequest || !isIntermediateStatus) return null;
    const requestStatus = leaveRequest.requestStatus;
    if (MANAGER_PENDING_STATUSES.has(requestStatus)) {
      return leaveRequest.approverName || 'Manager';
    }
    if (ADMINISTRATIV_PENDING_STATUSES.has(requestStatus)) {
      return 'Administrativ';
    }
    if (requestStatus === 'RETURNED_FOR_CORRECTION') {
      return `${leaveRequest.employeeName} (returned for correction)`;
    }
    return null;
  })();

  const leaveRequestView = (
    <div className="leave-request-view-container">
      <Typography.Paragraph>
        <strong>Employee:</strong> {leaveRequest?.employeeName}
      </Typography.Paragraph>
      <Typography.Paragraph>
        <strong>Type:</strong>{' '}
        {leaveRequest ? formatEnumLabel(leaveRequest.requestType) : ''}
      </Typography.Paragraph>
      <Typography.Paragraph>
        <strong>Status:</strong>{' '}
        {leaveRequest && (
          <Tag className={getRequestStatusType(leaveRequest.requestStatus)}>
            {formatEnumLabel(leaveRequest.requestStatus)}
          </Tag>
        )}
      </Typography.Paragraph>
      {!!assignedTo && (
        <Typography.Paragraph>
          <strong>Task assigned to:</strong> {assignedTo}
        </Typography.Paragraph>
      )}
      <Typography.Paragraph>
        <strong>Period:</strong>{' '}
        {leaveRequest?.startDate
          ? formatDateAsUTC(new Date(leaveRequest.startDate))
          : 'N/A'}
        -
        {leaveRequest?.endDate
          ? formatDateAsUTC(new Date(leaveRequest.endDate))
          : 'N/A'}
      </Typography.Paragraph>
      <Typography.Paragraph>
        <strong>Leave days:</strong>
      </Typography.Paragraph>
      <div className="leave-days">
        {leaveRequest?.leaveDays.map((lq) => (
          <Typography.Text key={lq}>
            {formatDateAsUTC(new Date(lq))}
          </Typography.Text>
        ))}
      </div>
      <Typography.Paragraph>
        <strong>Employee comment:</strong> {leaveRequest?.comment}
      </Typography.Paragraph>
      <Typography.Paragraph>
        <strong>Substitute employee:</strong>{' '}
        {leaveRequest?.substituteName ? leaveRequest.substituteName : 'N/A'}
      </Typography.Paragraph>
      <Typography.Paragraph>
        <strong>Current year remaining days:</strong>{' '}
        {leaveRequest?.currentYearRemainingDays}
      </Typography.Paragraph>
      {!isIntermediateStatus && !!leaveRequest?.approverName && (
        <Typography.Paragraph>
          <strong>Approver:</strong> {leaveRequest.approverName}
        </Typography.Paragraph>
      )}
      {!!leaveRequest?.administrativApproverName && (
        <Typography.Paragraph>
          <strong>Administrativ:</strong>{' '}
          {leaveRequest.administrativApproverName}
        </Typography.Paragraph>
      )}
      {!!leaveRequest?.documents?.length && (
        <Typography.Paragraph>
          <strong>Documents:</strong>{' '}
          <ViewDocumentsBtn documents={leaveRequest.documents} />
        </Typography.Paragraph>
      )}

      {isActionable ? (
        <div className="request-actions">
          <Tooltip title="Approve request">
            <Button
              type="text"
              className="status-action-btn"
              icon={<CheckOutlined />}
              onClick={() => {
                setStatus(RequestStatus.APPROVE);
                setIsRequestStatusOpen(true);
              }}
            >
              Sign and Approve request
            </Button>
          </Tooltip>
          <Tooltip title="Reject request">
            <Button
              type="text"
              className="status-action-btn"
              danger
              icon={<CloseOutlined />}
              onClick={() => {
                setStatus(RequestStatus.REJECT);
                setIsRequestStatusOpen(true);
              }}
            >
              Reject request
            </Button>
          </Tooltip>
        </div>
      ) : (
        leaveRequest &&
        !forceReadOnly && (
          <Typography.Paragraph type="secondary">
            This request isn&apos;t awaiting your decision right now. Open the
            app for any further action.
          </Typography.Paragraph>
        )
      )}

      {isRequestStatusOpen && (
        <RequestStatusModal
          isOpen={isRequestStatusOpen}
          setIsOpen={setIsRequestStatusOpen}
          request={leaveRequest ? leaveRequest : null}
          status={status}
          source={'default'}
          onCancelConfirm={fetchLeaveRequest}
        />
      )}
    </div>
  );

  if (!authUser) {
    return <Navigate to="/login" replace />;
  }

  if (forbidden) {
    return <p>You do not have permission to view this leave request.</p>;
  }

  return (
    <div>
      <Typography.Title level={1}>Leave Request #{id}</Typography.Title>
      {loading ? (
        <div className="loading-container">
          <Spin size="small" />
          <p>Loading leave request...</p>
        </div>
      ) : (
        leaveRequestView
      )}
    </div>
  );
};

export default LeaveRequestView;
